<template>
  <Card class="mb-4" :class="{ 'card-loading': isUpdating }" data-testid="partner-switcher">
    <template #title>
      <div class="d-flex align-center gap-4">
        <span>Partner Switcher</span>
        <v-chip
          color="red-darken-3"
          size="x-small"
          variant="flat"
          class="text-uppercase ml-2"
          style="font-size: 0.65rem; line-height: 1.2; font-weight: 700"
          prepend-icon="mdi-shield-account"
        >
          Admin Only
        </v-chip>
      </div>
    </template>

    <div class="d-flex flex-column gap-3">
      <!-- Current Partner Display -->
      <div v-if="currentPartner" class="current-partner pa-3 bg-grey-lighten-5 rounded my-2">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h4 class="text-body-1 font-weight-medium mb-1">Current Partner</h4>
            <p class="text-body-2 text-grey-darken-1 mb-0">{{ currentPartner.name }}</p>
          </div>
          <v-chip color="success" size="small">Active</v-chip>
        </div>
      </div>

      <v-text-field
        v-model="searchQuery"
        label="Search partners..."
        variant="outlined"
        density="compact"
        :loading="isSearching"
        :disabled="isUpdating"
        @input="onSearchInput"
        clearable
        hide-details
        data-testid="partner-search-input"
        :readonly="isUpdating"
      >
        <template #prepend-inner>
          <v-icon size="small">mdi-magnify</v-icon>
        </template>
      </v-text-field>

      <!-- Debug: Show partners count -->
      <div v-if="partners.length > 0" class="mb-2 text-caption">Found {{ partners.length }} partners</div>

      <div v-if="partners.length > 0" class="partner-dropdown">
        <div
          v-for="partner in partners"
          :key="partner.id"
          class="partner-item"
          @click="selectPartner(partner)"
          :class="{
            'partner-item--disabled': isUpdating && selectedPartnerId !== partner.id,
            'partner-item--updating': isUpdating && selectedPartnerId === partner.id
          }"
          data-testid="partner-option"
        >
          <div class="partner-item__content">
            <div class="partner-item__avatar">
              <v-icon size="16" color="#666">mdi-domain</v-icon>
            </div>
            <div class="partner-item__info">
              <div class="partner-item__name">{{ partner.name }}</div>
              <div class="partner-item__subtitle">{{ partner.description || `Partner ID: ${partner.id}` }}</div>
            </div>
            <div v-if="isUpdating && selectedPartnerId === partner.id" class="partner-item__loading">
              <div class="loading-spinner" />
              <span class="loading-text-inline">Updating...</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="searchQuery && !isSearching" class="text-center py-4">
        <v-icon size="large" color="grey-lighten-1">mdi-magnify</v-icon>
        <p class="text-body-2 text-grey-darken-1 mt-2">
          <span v-if="searchQuery.length < 3"> Type at least 3 characters to search </span>
          <span v-else> No partners found for "{{ searchQuery }}" </span>
        </p>
      </div>

      <div v-if="error" class="error-message">
        <v-alert type="error" density="compact" closable @click:close="clearError">
          {{ error }}
        </v-alert>
      </div>
    </div>
  </Card>
</template>

<script>
import { defineComponent } from 'vue';
import Card from './ui/Card.vue';
import { useUserStore } from '@/stores/user';
import { useUiStore } from '@/stores/ui';
import { SessionInitializer } from '../mixins/sessionInitializer';

export default defineComponent({
  name: 'PartnerSwitcher',
  mixins: [SessionInitializer],
  components: {
    Card
  },
  data() {
    return {
      searchQuery: '',
      partners: [],
      isSearching: false,
      isUpdating: false,
      selectedPartnerId: null,
      error: null,
      searchTimeout: null
    };
  },
  computed: {
    currentPartner() {
      const userStore = useUserStore();
      const learnerData = userStore.apis?.learnerData || this.$conn?.learnerData;

      // Check multiple possible field names for partner info
      const partnerName = learnerData?._partnerName || learnerData?.partnerName || learnerData?.partner?.name;
      const partnerId = learnerData?._partnerId || learnerData?.partnerId || learnerData?.partner?.id;

      // If no partner data is found, return placeholder
      if (!partnerName && !partnerId) {
        return {
          id: null,
          name: 'No partner assigned'
        };
      }

      return {
        id: partnerId,
        name: partnerName || `Partner ID: ${partnerId}` || 'Current Partner'
      };
    }
  },
  async mounted() {
    // Initialize session like Dashboard does
    try {
      await this.init('partner-switcher', async () => {
        // Force reactivity update after session initialization
        this.$forceUpdate();
      });
    } catch (error) {
      // Silently handle initialization errors
      console.warn('Could not initialize partner switcher session');
    }
  },

  methods: {
    onSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Clear results if query is empty
      if (!this.searchQuery?.trim()) {
        this.partners = [];
        return;
      }

      // Debounce the search - wait 500ms for better UX
      this.searchTimeout = setTimeout(() => {
        this.searchPartners();
      }, 500);
    },

    async searchPartners() {
      if (!this.searchQuery?.trim() || this.searchQuery.trim().length < 3) return;

      this.isSearching = true;
      this.error = null;

      try {
        // Use connector service for API calls (it handles authentication properly)
        const response = await this.$conn._doConnect(
          `/v2/api/partners/search?query=${encodeURIComponent(this.searchQuery)}`,
          'GET',
          null,
          { skipLoading: true }
        );

        // Parse partners from Axios response structure
        this.partners = response?.data?.message?.partners || response?.data?.data?.partners || [];

        // Debug logging
        this.logError('Partner search success', {
          partnersCount: this.partners.length,
          firstPartner: this.partners[0]?.name
        });
      } catch (error) {
        this.logError('Partner search error', error);
        this.error = 'Failed to search partners. Please try again.';
        this.partners = [];
      } finally {
        this.isSearching = false;
      }
    },

    async selectPartner(partner) {
      if (this.isUpdating) return;

      this.isUpdating = true;
      this.selectedPartnerId = partner.id;
      this.error = null;

      const uiStore = useUiStore();
      let success = false;

      try {
        const userStore = useUserStore();
        const learnerData = userStore.apis?.learnerData || this.$conn?.learnerData;

        if (!learnerData?._learnerId) {
          throw new Error('Learner ID not found');
        }

        // Make patch call to learner API to update partner field (v2 endpoint)
        const response = await this.$conn._doConnect(
          `/v2/api/learners/${learnerData._learnerId}`,
          'PATCH',
          {
            partnerId: partner.id
          },
          { skipLoading: true }
        );

        if (response?.data) {
          success = true;

          // Show success notification
          uiStore.showSuccessNotification('Partner Updated', `Successfully switched to ${partner.name}`);

          // Emit event to parent to potentially refresh data
          this.$emit('partner-changed', partner);

          // Keep loading state visible for longer, then navigate
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 3000);
        }
      } catch (error) {
        this.logError('Partner update error', error);
        this.error = error?.response?.data?.message || 'Failed to update partner. Please try again.';

        uiStore.showErrorNotification('Update Failed', 'Failed to switch partner. Please try again.');
      } finally {
        // Only clear loading state on error
        if (!success) {
          this.isUpdating = false;
          this.selectedPartnerId = null;
        }
        // On success, loading state will be cleared by page redirect
      }
    },

    clearError() {
      this.error = null;
    },

    logError(message, error) {
      // Use Vue's built-in error logging or console as fallback
      if (this.$logger) {
        this.$logger.error(message, error);
      } else if (console && console.error) {
        // eslint-disable-next-line no-console
        console.error(message, error);
      }
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
});
</script>

<style scoped>
.current-partner {
  border: 1px solid #e0e0e0;
}

.error-message {
  margin-top: 8px;
}

/* Card loading state */
.card-loading {
  position: relative;
}

.partner-dropdown {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.partner-item {
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.partner-item:last-child {
  border-bottom: none;
}

.partner-item:hover:not(.partner-item--disabled) {
  background-color: #f8f9fa;
}

.partner-item--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.partner-item--updating {
  background-color: #f0f8ff;
  border-color: #8c1d40;
  cursor: default;
  pointer-events: none;
}

.partner-item__content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
}

.partner-item__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f5f5f5;
  border-radius: 6px;
  flex-shrink: 0;
}

.partner-item__info {
  flex: 1;
  min-width: 0;
}

.partner-item__name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;
  margin-bottom: 2px;
}

.partner-item__subtitle {
  font-size: 12px;
  color: #666;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.partner-item__loading {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #8c1d40;
}

.loading-text-inline {
  font-size: 12px;
  font-weight: 500;
  color: #8c1d40;
}

.loading-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
